import Dexie, { Table } from 'dexie';

export interface Company {
  id?: number;
  name: string;
  financialYearStart: string;
  booksBeginFrom: string;
  address?: string;
  gstin?: string;
  state?: string;
  pincode?: string;
  mobile?: string;
  email?: string;
  businessNature?: BusinessNature;
  isActive: number; // 0 or 1
  lastEntryDate?: string;
  appearance?: 'light' | 'dark' | 'system';
  language?: 'en' | 'hi' | 'gu' | 'mr' | 'ta';
}

export type BusinessNature = 'general' | 'pharmacy' | 'restaurant' | 'jewellery' | 'realestate' | 'education' | 'transport' | 'hotel';

export interface AccountGroup {
  id?: number;
  companyId: number;
  name: string;
  parentGroupId: number | null; // null for primary groups
  nature: 'Assets' | 'Liabilities' | 'Income' | 'Expenses';
  isDefault: boolean;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

export interface Ledger {
  id?: number;
  companyId: number;
  name: string;
  alias?: string;
  groupId: number | null; // null for special ledgers like P&L
  openingBalance: number;
  balanceType: 'Dr' | 'Cr';
  isDefault: boolean;
  gstRegistrationType?: 'Regular' | 'Composition' | 'Unregistered' | 'Consumer';
  gstin?: string;
  pan?: string;
  address?: string;
  state?: string;
  pincode?: string;
  taxRate?: number; // ADDED for GST Tax System Refactor
  // Extended fields
  mailingName?: string;
  billByBill?: 'Yes' | 'No';
  creditPeriod?: string;
  creditLimit?: number;    // Max outstanding allowed (0 = unlimited)
  allowAdv?: 'Yes' | 'No';
  bankName?: string;
  bankAcct?: string;
  bankIfsc?: string;
  bankBranch?: string;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

export interface BillAllocation {
  billId:      number;  // voucherId of the source invoice
  billNo:      string;  // referenceNumber/voucherNumber of invoice
  billDate:    string;  // date of invoice
  allocAmount: number;  // amount applied to this bill
  type: 'against_ref' | 'on_account';
}

export interface Voucher {
  id?: number;
  companyId: number;
  type: string; // Keep for legacy, but we will rely on voucherTypeId
  voucherTypeId?: number; // ADDED
  currencyId?: number; // ADDED
  date: string;
  voucherNumber: string; // Auto-generated numeric system sequence (No longer manually edited!)
  referenceNumber?: string; // USER INPUT / MANUAL
  sysId?: string; // Kept for backwards compatibility
  narration: string;
  deleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
  billAllocations?: BillAllocation[];  // Bill-by-bill payment/receipt allocations
}

export interface VoucherEntry {
  id?: number;
  voucherId: number;
  ledgerId: number;
  type: 'Dr' | 'Cr';
  amount: number;
  currencyId?: number; // ADDED
}

export interface InventoryEntry {
  id?: number;
  voucherId: number;
  stockItemId: number;
  locationId: number;
  quantity: number;
  rate: number;
  amount: number;
  currencyId?: number;
  discPercent?: number;
  // Pharmacy-specific (batch tracking)
  batchNumber?: string;
  expiryDate?: string;   // YYYY-MM format
  mfgDate?: string;      // YYYY-MM format
  mrp?: number;
}

export interface StockGroup {
  id?: number;
  companyId: number;
  name: string;
  parentGroupId: number | null;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

export interface StockItem {
  id?: number;
  companyId: number;
  name: string;
  stockGroupId: number | null;
  stockCategoryId: number | null;
  unitId: number;
  openingBalance: number;
  openingRate: number;
  openingValue: number;
  gstRate?: number; // e.g. 0, 5, 12, 18, 28
  hsn?: string;     // HSN/SAC code — required for GSTR-1 HSN summary
  reorderLevel?: number;
  minStockLevel?: number;
  // Pharmacy-specific fields (used when businessNature === 'pharmacy')
  manufacturer?: string;
  saltComposition?: string;
  schedule?: 'none' | 'H' | 'H1' | 'X' | 'G' | 'narcotics';
  mrp?: number;
  pack?: string;          // e.g. "1x10", "1x15"
  rackLocation?: string;  // e.g. "Rack 3, Shelf B"
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

export interface Unit {
  id?: number;
  companyId: number;
  symbol: string;
  formalName: string;
  decimalPlaces: number;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

export interface Location {
  id?: number;
  companyId: number;
  name: string;
  parentLocationId: number | null;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

export interface StockCategory {
  id?: number;
  companyId: number;
  name: string;
  parentCategoryId: number | null;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

export interface SalaryComponent {
  id?: number;
  companyId: number;
  name: string;
  type: 'Allowance' | 'Deduction';
  valueType: 'Fixed' | 'Percentage';
  percentageValue?: number; // e.g., 12 for 12%
}

export interface Employee {
  id?: number;
  companyId: number;
  employeeId: string;
  name: string;
  department: string;
  designation: string;
  joiningDate: string;
  status: 'Active' | 'Inactive';
  bankName?: string;
  bankAccountNo?: string;
  ifscCode?: string;
  basicSalary?: number;
  salaryComponents?: {
    componentId: number;
    customAmount?: number; // Override default if fixed
  }[];
  // Backwards compatibility
  salaryStructure?: {
    basic: number;
    hra: number;
    conveyance: number;
    otherAllowances: number;
    pfDeduction: number;
    esiDeduction: number;
    tdsDeduction: number;
  };
}

export interface EmployeeLeave {
  id?: number;
  companyId: number;
  employeeId: number;
  startDate: string;
  endDate: string;
  type: 'Sick' | 'Casual' | 'Earned' | 'Unpaid';
  status: 'Pending' | 'Approved' | 'Rejected';
  reason: string;
  appliedOn: string;
}

export interface EmployeeLoan {
  id?: number;
  companyId: number;
  employeeId: number;
  amount: number;
  emiAmount: number;
  totalMonths: number;
  monthsPaid: number;
  startDate: string;
  status: 'Active' | 'Closed';
}

export interface EmployeeAttendance {
  id?: number;
  companyId: number;
  employeeId: number;
  date: string;
  status: 'Present' | 'Absent' | 'Half Day' | 'On Leave';
  inTime?: string;
  outTime?: string;
}

export interface Payroll {
  id?: number;
  companyId: number;
  employeeId: number;
  month: number;
  year: number;
  basicSalary: number;
  earnings: { name: string; amount: number }[];
  deductions: { name: string; amount: number }[];
  netSalary: number;
  status: 'Processed';
  voucherId?: number;
  processedDate?: string;
}

export interface Currency {
  id?: number;
  companyId: number;
  name: string;
  symbol: string;
  code: string;
  isBaseCurrency: boolean;
  exchangeRate: number;
  createdAt: string;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

export interface VoucherType {
  id?: number;
  companyId: number;
  name: string;
  prefix?: string;
  numberingMethod: 'auto' | 'manual';
  startingNumber: number;
  currentNumber: number;
  affectsStock: boolean;
  stockDirection?: 'In' | 'Out' | 'None';
  isSystem: boolean;
  createdAt: string;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

export interface AuditLog {
  id?: number;
  companyId: number;
  actionType: 'CREATE' | 'UPDATE' | 'DELETE' | 'CREATE_VOUCHER' | 'UPDATE_VOUCHER' | 'DELETE_VOUCHER';
  entityType: 'voucher' | 'ledger' | 'stockItem' | 'currency' | 'voucherType' | 'company';
  entityId: number;
  timestamp: string;
  details?: string;
  userId?: number;
}

export interface DeletedVoucher {
  id?: number;
  companyId: number;
  voucherTypeId: number;
  type: string;
  voucherNumber: string;
  referenceNumber?: string;
  sysId?: string; // Auto-generated rigid system sequence
  deletedAt: string;
}


export interface Quotation {
  id?: number;
  companyId: number;
  quotationNumber: string;
  date: string;
  // Company details (manual per quotation)
  companyName: string;
  companyAddress: string;
  companyGstin: string;
  companyState: string;
  companyStateCode: string;
  companyMobile: string;
  // Party (Bill To)
  partyName: string;
  partyAddress: string;
  // Items - fully manual
  items: { name: string; hsn: string; qty: number; rate: number; amount: number }[];
  // Tax
  cgstRate: number;
  cgstAmount: number;
  sgstRate: number;
  sgstAmount: number;
  // Totals
  subtotal: number;
  totalAmount: number;
  // Footer
  termsAndConditions: string;
  bankName: string;
  bankAccountNo: string;
  bankIfsc: string;
  signatureImage: string;
  narration: string;
  status: 'open' | 'converted' | 'cancelled';
  convertedToVoucherId: number | null;
  // Legacy compat (optional)
  partyLedgerId?: number;
  salesLedgerId?: number | null;
  charges?: { ledgerId: number; amount: number }[];
}

export interface CostCentre {
  id?: number;
  companyId: number;
  name: string;
  parentCentreId: number | null;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

export class AccountingDB extends Dexie {
  companies!: Table<Company, number>;
  groups!: Table<AccountGroup, number>;
  ledgers!: Table<Ledger, number>;
  vouchers!: Table<Voucher, number>;
  deletedVouchers!: Table<DeletedVoucher, number>;
  voucherEntries!: Table<VoucherEntry, number>;
  stockGroups!: Table<StockGroup, number>;
  stockCategories!: Table<StockCategory, number>;
  stockItems!: Table<StockItem, number>;
  units!: Table<Unit, number>;
  locations!: Table<Location, number>;
  inventoryEntries!: Table<InventoryEntry, number>;
  employees!: Table<Employee, number>;
  employeeLeaves!: Table<EmployeeLeave, number>; // ADDED
  employeeLoans!: Table<EmployeeLoan, number>; // ADDED
  employeeAttendance!: Table<EmployeeAttendance, number>; // ADDED
  payroll!: Table<Payroll, number>;
  salaryComponents!: Table<SalaryComponent, number>;
  currencies!: Table<Currency, number>;
  voucherTypes!: Table<VoucherType, number>;
  auditLogs!: Table<AuditLog, number>; // ADDED
  costCentres!: Table<CostCentre, number>;
  quotations!: Table<Quotation, number>; // ADDED v16

  constructor() {
    super('AccountingDB');
    this.version(11).stores({
      companies: '++id, name, gstin, isActive',
      groups: '++id, companyId, name, parentGroupId',
      ledgers: '++id, companyId, name, groupId, gstin',
      vouchers: '++id, companyId, type, voucherTypeId, date, voucherNumber',
      deletedVouchers: '++id, companyId, voucherTypeId, voucherNumber',
      voucherEntries: '++id, voucherId, ledgerId',
      stockGroups: '++id, companyId, name, parentGroupId',
      stockCategories: '++id, companyId, name, parentCategoryId',
      stockItems: '++id, companyId, name, stockGroupId, stockCategoryId',
      units: '++id, companyId, symbol',
      locations: '++id, companyId, name',
      inventoryEntries: '++id, voucherId, stockItemId, locationId',
      employees: '++id, companyId, employeeId, name, status',
      employeeLeaves: '++id, companyId, employeeId, status, startDate', // ADDED
      employeeLoans: '++id, companyId, employeeId, status', // ADDED
      employeeAttendance: '++id, companyId, employeeId, date', // ADDED
      salaryComponents: '++id, companyId, name, type',
      payroll: '++id, companyId, employeeId, month, year, status',
      currencies: '++id, companyId, code, isBaseCurrency',
      voucherTypes: '++id, companyId, name',
      auditLogs: '++id, companyId, actionType, entityType, entityId, timestamp'
    }).upgrade(async (tx) => {
      // Data Migration for legacy salaryStructure to salaryComponents
      const employees = await tx.table('employees').toArray();
      for (const emp of employees) {
        if (emp.salaryStructure && (!emp.salaryComponents || emp.salaryComponents.length === 0)) {
          // Perform legacy migration
          const newComponents: { componentId: number; customAmount?: number }[] = [];
          
          // Helper to find or create component in same tx
          const getOrCreateComponent = async (name: string, type: 'Allowance' | 'Deduction') => {
            let comp = await tx.table('salaryComponents').where({ companyId: emp.companyId, name, type }).first();
            if (!comp) {
              const compId = await tx.table('salaryComponents').add({
                companyId: emp.companyId,
                name,
                type,
                valueType: 'Fixed'
              });
              return compId;
            }
            return comp.id!;
          };

          const s = emp.salaryStructure;
          emp.basicSalary = s.basic || 0;
          
          if (s.hra > 0) newComponents.push({ componentId: await getOrCreateComponent('HRA', 'Allowance'), customAmount: s.hra });
          if (s.conveyance > 0) newComponents.push({ componentId: await getOrCreateComponent('Conveyance', 'Allowance'), customAmount: s.conveyance });
          if (s.otherAllowances > 0) newComponents.push({ componentId: await getOrCreateComponent('Other Allowances', 'Allowance'), customAmount: s.otherAllowances });
          
          if (s.pfDeduction > 0) newComponents.push({ componentId: await getOrCreateComponent('PF', 'Deduction'), customAmount: s.pfDeduction });
          if (s.esiDeduction > 0) newComponents.push({ componentId: await getOrCreateComponent('ESI', 'Deduction'), customAmount: s.esiDeduction });
          if (s.tdsDeduction > 0) newComponents.push({ componentId: await getOrCreateComponent('TDS', 'Deduction'), customAmount: s.tdsDeduction });
          
          emp.salaryComponents = newComponents;
          
          // Keep salaryStructure for older UI if any, or delete it. Since we say "legacy", let's keep it safely or delete it. It's marked optional.
          // delete emp.salaryStructure; 
          
          await tx.table('employees').put(emp);
        }
      }
    });

    // Version 12 — adds compound indexes so seed.ts idempotency checks work correctly
    // (Dexie where({a,b}) only works with compound index [a+b], not two separate indexes)
    this.version(12).stores({
      companies: '++id, name, gstin, isActive',
      groups: '++id, companyId, name, parentGroupId, [companyId+name]',
      ledgers: '++id, companyId, name, groupId, gstin, [companyId+name]',
      vouchers: '++id, companyId, type, voucherTypeId, date, voucherNumber',
      deletedVouchers: '++id, companyId, voucherTypeId, voucherNumber',
      voucherEntries: '++id, voucherId, ledgerId',
      stockGroups: '++id, companyId, name, parentGroupId, [companyId+name]',
      stockCategories: '++id, companyId, name, parentCategoryId, [companyId+name]',
      stockItems: '++id, companyId, name, stockGroupId, stockCategoryId, [companyId+name]',
      units: '++id, companyId, symbol, [companyId+symbol]',
      locations: '++id, companyId, name, [companyId+name]',
      inventoryEntries: '++id, voucherId, stockItemId, locationId',
      employees: '++id, companyId, employeeId, name, status',
      employeeLeaves: '++id, companyId, employeeId, status, startDate',
      employeeLoans: '++id, companyId, employeeId, status',
      employeeAttendance: '++id, companyId, employeeId, date',
      salaryComponents: '++id, companyId, name, type, [companyId+name]',
      payroll: '++id, companyId, employeeId, month, year, status',
      currencies: '++id, companyId, code, isBaseCurrency, [companyId+code]',
      voucherTypes: '++id, companyId, name, [companyId+name]',
      auditLogs: '++id, companyId, actionType, entityType, entityId, timestamp'
    });

    // Version 13 — re-seeds missing master data (units, voucher types, currencies, etc.)
    // for companies created before version 12 compound indexes were added.
    // The upgrade function runs seedCompany for every existing company to fill gaps.
    this.version(13).stores({
      companies: '++id, name, gstin, isActive',
      groups: '++id, companyId, name, parentGroupId, [companyId+name]',
      ledgers: '++id, companyId, name, groupId, gstin, [companyId+name]',
      vouchers: '++id, companyId, type, voucherTypeId, date, voucherNumber',
      deletedVouchers: '++id, companyId, voucherTypeId, voucherNumber',
      voucherEntries: '++id, voucherId, ledgerId',
      stockGroups: '++id, companyId, name, parentGroupId, [companyId+name]',
      stockCategories: '++id, companyId, name, parentCategoryId, [companyId+name]',
      stockItems: '++id, companyId, name, stockGroupId, stockCategoryId, [companyId+name]',
      units: '++id, companyId, symbol, [companyId+symbol]',
      locations: '++id, companyId, name, [companyId+name]',
      inventoryEntries: '++id, voucherId, stockItemId, locationId',
      employees: '++id, companyId, employeeId, name, status',
      employeeLeaves: '++id, companyId, employeeId, status, startDate',
      employeeLoans: '++id, companyId, employeeId, status',
      employeeAttendance: '++id, companyId, employeeId, date',
      salaryComponents: '++id, companyId, name, type, [companyId+name]',
      payroll: '++id, companyId, employeeId, month, year, status',
      currencies: '++id, companyId, code, isBaseCurrency, [companyId+code]',
      voucherTypes: '++id, companyId, name, [companyId+name]',
      auditLogs: '++id, companyId, actionType, entityType, entityId, timestamp'
    }).upgrade(async (tx) => {
      // Re-seed all existing companies to fill any missing master data
      // (units, voucher types, currencies, locations, stock groups, etc.)
      const companies = await tx.table('companies').toArray();
      for (const company of companies) {
        if (!company.id) continue;
        const now = new Date().toISOString();

        // Units
        const unitDefs = [
          { symbol: 'Nos', formalName: 'Numbers',   decimalPlaces: 0 },
          { symbol: 'Pcs', formalName: 'Pieces',    decimalPlaces: 0 },
          { symbol: 'Kgs', formalName: 'Kilograms', decimalPlaces: 3 },
          { symbol: 'Ltr', formalName: 'Liters',    decimalPlaces: 3 },
          { symbol: 'Mtr', formalName: 'Meters',    decimalPlaces: 2 },
        ];
        for (const u of unitDefs) {
          const exists = await tx.table('units').where('[companyId+symbol]').equals([company.id, u.symbol]).first();
          if (!exists) await tx.table('units').add({ companyId: company.id, ...u });
        }

        // Voucher Types
        const vtDefs = [
          { name: 'Contra',      affectsStock: false, stockDirection: 'None' },
          { name: 'Receipt',     affectsStock: false, stockDirection: 'None' },
          { name: 'Payment',     affectsStock: false, stockDirection: 'None' },
          { name: 'Journal',     affectsStock: false, stockDirection: 'None' },
          { name: 'Sales',       affectsStock: true,  stockDirection: 'Out' },
          { name: 'Purchase',    affectsStock: true,  stockDirection: 'In' },
          { name: 'Credit Note', affectsStock: true,  stockDirection: 'In' },
          { name: 'Debit Note',  affectsStock: true,  stockDirection: 'Out' },
        ];
        for (const vt of vtDefs) {
          const exists = await tx.table('voucherTypes').where('[companyId+name]').equals([company.id, vt.name]).first();
          if (!exists) await tx.table('voucherTypes').add({ companyId: company.id, ...vt, numberingMethod: 'auto', startingNumber: 1, currentNumber: 1, isSystem: true, createdAt: now });
        }

        // Currencies
        const existingINR = await tx.table('currencies').where('[companyId+code]').equals([company.id, 'INR']).first();
        if (!existingINR) await tx.table('currencies').add({ companyId: company.id, name: 'Indian Rupee', symbol: '₹', code: 'INR', isBaseCurrency: true, exchangeRate: 1, createdAt: now });

        // Default Location
        const existingLoc = await tx.table('locations').where('[companyId+name]').equals([company.id, 'Main Location']).first();
        if (!existingLoc) await tx.table('locations').add({ companyId: company.id, name: 'Main Location', parentLocationId: null });

        // Stock Groups
        for (const sgName of ['Electronics', 'Furniture', 'Stationery', 'Raw Materials']) {
          const exists = await tx.table('stockGroups').where('[companyId+name]').equals([company.id, sgName]).first();
          if (!exists) await tx.table('stockGroups').add({ companyId: company.id, name: sgName, parentGroupId: null });
        }

        // Stock Categories
        const existingCat = await tx.table('stockCategories').where('[companyId+name]').equals([company.id, 'Primary']).first();
        if (!existingCat) await tx.table('stockCategories').add({ companyId: company.id, name: 'Primary', parentCategoryId: null });

        // Salary Components
        const salaryDefs = [
          { name: 'HRA',        type: 'Allowance', valueType: 'Percentage', percentageValue: 40 },
          { name: 'Conveyance', type: 'Allowance', valueType: 'Fixed' },
          { name: 'PF',         type: 'Deduction', valueType: 'Percentage', percentageValue: 12 },
          { name: 'ESI',        type: 'Deduction', valueType: 'Percentage', percentageValue: 0.75 },
          { name: 'TDS',        type: 'Deduction', valueType: 'Fixed' },
        ];
        for (const sc of salaryDefs) {
          const exists = await tx.table('salaryComponents').where('[companyId+name]').equals([company.id, sc.name]).first();
          if (!exists) await tx.table('salaryComponents').add({ companyId: company.id, ...sc });
        }

        // Core Groups needed for ledgers
        const groupDefs = [
          { name: 'Capital Account',     parentGroupId: null, nature: 'Liabilities' },
          { name: 'Current Assets',      parentGroupId: null, nature: 'Assets' },
          { name: 'Current Liabilities', parentGroupId: null, nature: 'Liabilities' },
          { name: 'Fixed Assets',        parentGroupId: null, nature: 'Assets' },
          { name: 'Sales Accounts',      parentGroupId: null, nature: 'Income' },
          { name: 'Purchase Accounts',   parentGroupId: null, nature: 'Expenses' },
          { name: 'Direct Expenses',     parentGroupId: null, nature: 'Expenses' },
          { name: 'Indirect Expenses',   parentGroupId: null, nature: 'Expenses' },
          { name: 'Indirect Income',     parentGroupId: null, nature: 'Income' },
          { name: 'Direct Income',       parentGroupId: null, nature: 'Income' },
        ];
        const groupIdMap: Record<string, number> = {};
        for (const g of groupDefs) {
          let existing = await tx.table('groups').where('[companyId+name]').equals([company.id, g.name]).first();
          if (!existing) {
            const gId = await tx.table('groups').add({ companyId: company.id, ...g, isDefault: true });
            groupIdMap[g.name] = gId as number;
          } else {
            groupIdMap[g.name] = existing.id!;
          }
        }

        // Sub-groups
        const subGroupDefs = [
          { name: 'Bank Accounts',    parentName: 'Current Assets',      nature: 'Assets' },
          { name: 'Cash-in-Hand',     parentName: 'Current Assets',      nature: 'Assets' },
          { name: 'Sundry Debtors',   parentName: 'Current Assets',      nature: 'Assets' },
          { name: 'Sundry Creditors', parentName: 'Current Liabilities', nature: 'Liabilities' },
          { name: 'Duties & Taxes',   parentName: 'Current Liabilities', nature: 'Liabilities' },
        ];
        for (const sg of subGroupDefs) {
          let existing = await tx.table('groups').where('[companyId+name]').equals([company.id, sg.name]).first();
          if (!existing) {
            const parentId = groupIdMap[sg.parentName] || null;
            const gId = await tx.table('groups').add({ companyId: company.id, name: sg.name, parentGroupId: parentId, nature: sg.nature, isDefault: true });
            groupIdMap[sg.name] = gId as number;
          } else {
            groupIdMap[sg.name] = existing.id!;
          }
        }

        // Default Ledgers
        const ledgerDefs = [
          { name: 'Cash',              groupName: 'Cash-in-Hand',     balanceType: 'Dr' },
          { name: 'Bank A/c',          groupName: 'Bank Accounts',    balanceType: 'Dr' },
          { name: 'Profit & Loss A/c', groupName: null,               balanceType: 'Cr' },
          { name: 'Sales A/c',         groupName: 'Sales Accounts',   balanceType: 'Cr' },
          { name: 'Purchase A/c',      groupName: 'Purchase Accounts', balanceType: 'Dr' },
          { name: 'CGST',              groupName: 'Duties & Taxes',   balanceType: 'Cr' },
          { name: 'SGST',              groupName: 'Duties & Taxes',   balanceType: 'Cr' },
          { name: 'IGST',              groupName: 'Duties & Taxes',   balanceType: 'Cr' },
          { name: 'CESS',              groupName: 'Duties & Taxes',   balanceType: 'Cr' },
        ];
        for (const l of ledgerDefs) {
          const exists = await tx.table('ledgers').where('[companyId+name]').equals([company.id, l.name]).first();
          if (!exists) {
            const gId = l.groupName ? (groupIdMap[l.groupName] || null) : null;
            await tx.table('ledgers').add({ companyId: company.id, name: l.name, groupId: gId, openingBalance: 0, balanceType: l.balanceType, isDefault: true });
          }
        }
      }
    });

    // Version 14 — Deduplication and application-level unique constraint support
    this.version(14).stores({
      companies: '++id, name, gstin, isActive',
      groups: '++id, companyId, name, parentGroupId, [companyId+name]',
      ledgers: '++id, companyId, name, groupId, gstin, [companyId+name]',
      vouchers: '++id, companyId, type, voucherTypeId, date, voucherNumber',
      deletedVouchers: '++id, companyId, voucherTypeId, voucherNumber',
      voucherEntries: '++id, voucherId, ledgerId',
      stockGroups: '++id, companyId, name, parentGroupId, [companyId+name]',
      stockCategories: '++id, companyId, name, parentCategoryId, [companyId+name]',
      stockItems: '++id, companyId, name, stockGroupId, stockCategoryId, [companyId+name]',
      units: '++id, companyId, symbol, [companyId+symbol]',
      locations: '++id, companyId, name, [companyId+name]',
      inventoryEntries: '++id, voucherId, stockItemId, locationId',
      employees: '++id, companyId, employeeId, name, status',
      employeeLeaves: '++id, companyId, employeeId, status, startDate',
      employeeLoans: '++id, companyId, employeeId, status',
      employeeAttendance: '++id, companyId, employeeId, date',
      salaryComponents: '++id, companyId, name, type, [companyId+name]',
      payroll: '++id, companyId, employeeId, month, year, status',
      currencies: '++id, companyId, code, isBaseCurrency, [companyId+code]',
      voucherTypes: '++id, companyId, name, [companyId+name]',
      auditLogs: '++id, companyId, actionType, entityType, entityId, timestamp'
    }).upgrade(async (tx) => {
      // Deduplicate before unique constraints are applied by dexie
      const deduplicate = async (tableName: string, keyProps: string[]) => {
        const records = await tx.table(tableName).toArray();
        const seen = new Set();
        for (const record of records) {
          const key = keyProps.map(p => (record as any)[p]).join('|||');
          if (seen.has(key)) {
            await tx.table(tableName).delete(record.id);
          } else {
            seen.add(key);
          }
        }
      };

      await deduplicate('companies', ['name']);
      await deduplicate('groups', ['companyId', 'name']);
      await deduplicate('ledgers', ['companyId', 'name']);
      await deduplicate('stockGroups', ['companyId', 'name']);
      await deduplicate('stockCategories', ['companyId', 'name']);
      await deduplicate('stockItems', ['companyId', 'name']);
      await deduplicate('units', ['companyId', 'symbol']);
      await deduplicate('locations', ['companyId', 'name']);
      await deduplicate('salaryComponents', ['companyId', 'name']);
      await deduplicate('currencies', ['companyId', 'code']);
      await deduplicate('voucherTypes', ['companyId', 'name']);
    });

    // Version 15 — Voucher Numbering System Fix
    this.version(15).stores({
      companies: '++id, name, gstin, isActive',
      groups: '++id, companyId, name, parentGroupId, [companyId+name]',
      ledgers: '++id, companyId, name, groupId, gstin, [companyId+name]',
      vouchers: '++id, companyId, type, voucherTypeId, date, voucherNumber',
      deletedVouchers: '++id, companyId, voucherTypeId, voucherNumber',
      voucherEntries: '++id, voucherId, ledgerId',
      stockGroups: '++id, companyId, name, parentGroupId, [companyId+name]',
      stockCategories: '++id, companyId, name, parentCategoryId, [companyId+name]',
      stockItems: '++id, companyId, name, stockGroupId, stockCategoryId, [companyId+name]',
      units: '++id, companyId, symbol, [companyId+symbol]',
      locations: '++id, companyId, name, [companyId+name]',
      inventoryEntries: '++id, voucherId, stockItemId, locationId',
      employees: '++id, companyId, employeeId, name, status',
      employeeLeaves: '++id, companyId, employeeId, status, startDate',
      employeeLoans: '++id, companyId, employeeId, status',
      employeeAttendance: '++id, companyId, employeeId, date',
      salaryComponents: '++id, companyId, name, type, [companyId+name]',
      payroll: '++id, companyId, employeeId, month, year, status',
      currencies: '++id, companyId, code, isBaseCurrency, [companyId+code]',
      voucherTypes: '++id, companyId, name, [companyId+name]',
      auditLogs: '++id, companyId, actionType, entityType, entityId, timestamp'
    }).upgrade(async (tx) => {
      // Data Migration: Split voucherNumber and referenceNumber
      const vouchers = await tx.table('vouchers').orderBy('date').toArray();
      const voucherTypesCounters: Record<number, number> = {};

      for (const v of vouchers) {
        if (!v.voucherTypeId) {
           // Fallback if somehow voucherTypeId is missing, find it by type and companyId
           const vt = await tx.table('voucherTypes').where({ companyId: v.companyId, name: v.type }).first();
           if (vt) v.voucherTypeId = vt.id;
           else continue;
        }
        
        if (!voucherTypesCounters[v.voucherTypeId!]) {
          voucherTypesCounters[v.voucherTypeId!] = 1;
        }

        const isPureNumber = /^\d+$/.test(v.voucherNumber);
        
        const updatePayload: any = {};

        // If it was corrupted by a user input string, move it to referenceNumber.
        updatePayload.referenceNumber = v.referenceNumber || (isPureNumber ? '' : v.voucherNumber);
        // Force sequential numbering to fix previous breakage
        updatePayload.voucherNumber = String(voucherTypesCounters[v.voucherTypeId!]++);
        
        await tx.table('vouchers').update(v.id, updatePayload);
      }

      // Update currentNumber for voucherTypes to match the new highest sequence
      for (const [typeId, nextNum] of Object.entries(voucherTypesCounters)) {
        await tx.table('voucherTypes').update(Number(typeId), { currentNumber: nextNum });
      }
    });
    // Version 16 — Adds Cost Centre table
    this.version(16).stores({
      companies: '++id, name, gstin, isActive',
      groups: '++id, companyId, name, parentGroupId, [companyId+name]',
      ledgers: '++id, companyId, name, groupId, gstin, [companyId+name]',
      vouchers: '++id, companyId, type, voucherTypeId, date, voucherNumber',
      deletedVouchers: '++id, companyId, voucherTypeId, voucherNumber',
      voucherEntries: '++id, voucherId, ledgerId',
      stockGroups: '++id, companyId, name, parentGroupId, [companyId+name]',
      stockCategories: '++id, companyId, name, parentCategoryId, [companyId+name]',
      stockItems: '++id, companyId, name, stockGroupId, stockCategoryId, [companyId+name]',
      units: '++id, companyId, symbol, [companyId+symbol]',
      locations: '++id, companyId, name, [companyId+name]',
      inventoryEntries: '++id, voucherId, stockItemId, locationId',
      employees: '++id, companyId, employeeId, name, status',
      employeeLeaves: '++id, companyId, employeeId, status, startDate',
      employeeLoans: '++id, companyId, employeeId, status',
      employeeAttendance: '++id, companyId, employeeId, date',
      salaryComponents: '++id, companyId, name, type, [companyId+name]',
      payroll: '++id, companyId, employeeId, month, year, status',
      currencies: '++id, companyId, code, isBaseCurrency, [companyId+code]',
      voucherTypes: '++id, companyId, name, [companyId+name]',
      auditLogs: '++id, companyId, actionType, entityType, entityId, timestamp',
      costCentres: '++id, companyId, name, parentCentreId, [companyId+name]'
    });
    // Version 17 — Adds HSN/SAC code field to stockItems (no index change, just data field)
    this.version(17).stores({
      companies: '++id, name, gstin, isActive',
      groups: '++id, companyId, name, parentGroupId, [companyId+name]',
      ledgers: '++id, companyId, name, groupId, gstin, [companyId+name]',
      vouchers: '++id, companyId, type, voucherTypeId, date, voucherNumber',
      deletedVouchers: '++id, companyId, voucherTypeId, voucherNumber',
      voucherEntries: '++id, voucherId, ledgerId',
      stockGroups: '++id, companyId, name, parentGroupId, [companyId+name]',
      stockCategories: '++id, companyId, name, parentCategoryId, [companyId+name]',
      stockItems: '++id, companyId, name, stockGroupId, stockCategoryId, [companyId+name]',
      units: '++id, companyId, symbol, [companyId+symbol]',
      locations: '++id, companyId, name, [companyId+name]',
      inventoryEntries: '++id, voucherId, stockItemId, locationId',
      employees: '++id, companyId, employeeId, name, status',
      employeeLeaves: '++id, companyId, employeeId, status, startDate',
      employeeLoans: '++id, companyId, employeeId, status',
      employeeAttendance: '++id, companyId, employeeId, date',
      salaryComponents: '++id, companyId, name, type, [companyId+name]',
      payroll: '++id, companyId, employeeId, month, year, status',
      currencies: '++id, companyId, code, isBaseCurrency, [companyId+code]',
      voucherTypes: '++id, companyId, name, [companyId+name]',
      auditLogs: '++id, companyId, actionType, entityType, entityId, timestamp',
      costCentres: '++id, companyId, name, parentCentreId, [companyId+name]'
    });

    // Version 18 — Adds Quotations table
    this.version(18).stores({
      companies: '++id, name, gstin, isActive',
      groups: '++id, companyId, name, parentGroupId, [companyId+name]',
      ledgers: '++id, companyId, name, groupId, gstin, [companyId+name]',
      vouchers: '++id, companyId, type, voucherTypeId, date, voucherNumber',
      deletedVouchers: '++id, companyId, voucherTypeId, voucherNumber',
      voucherEntries: '++id, voucherId, ledgerId',
      stockGroups: '++id, companyId, name, parentGroupId, [companyId+name]',
      stockCategories: '++id, companyId, name, parentCategoryId, [companyId+name]',
      stockItems: '++id, companyId, name, stockGroupId, stockCategoryId, [companyId+name]',
      units: '++id, companyId, symbol, [companyId+symbol]',
      locations: '++id, companyId, name, [companyId+name]',
      inventoryEntries: '++id, voucherId, stockItemId, locationId',
      employees: '++id, companyId, employeeId, name, status',
      employeeLeaves: '++id, companyId, employeeId, status, startDate',
      employeeLoans: '++id, companyId, employeeId, status',
      employeeAttendance: '++id, companyId, employeeId, date',
      salaryComponents: '++id, companyId, name, type, [companyId+name]',
      payroll: '++id, companyId, employeeId, month, year, status',
      currencies: '++id, companyId, code, isBaseCurrency, [companyId+code]',
      voucherTypes: '++id, companyId, name, [companyId+name]',
      auditLogs: '++id, companyId, actionType, entityType, entityId, timestamp',
      costCentres: '++id, companyId, name, parentCentreId, [companyId+name]',
      quotations: '++id, companyId, quotationNumber, date, partyLedgerId, status',
    });

    this.version(19).stores({
      companies: '++id, name, gstin, isActive',
      groups: '++id, companyId, name, parentGroupId, [companyId+name]',
      ledgers: '++id, companyId, name, groupId, gstin, [companyId+name]',
      vouchers: '++id, companyId, type, voucherTypeId, date, voucherNumber',
      deletedVouchers: '++id, companyId, voucherTypeId, voucherNumber',
      voucherEntries: '++id, voucherId, ledgerId',
      stockGroups: '++id, companyId, name, parentGroupId, [companyId+name]',
      stockCategories: '++id, companyId, name, parentCategoryId, [companyId+name]',
      stockItems: '++id, companyId, name, stockGroupId, stockCategoryId, [companyId+name]',
      units: '++id, companyId, symbol, [companyId+symbol]',
      locations: '++id, companyId, name, [companyId+name]',
      inventoryEntries: '++id, voucherId, stockItemId, locationId',
      employees: '++id, companyId, employeeId, name, status',
      employeeLeaves: '++id, companyId, employeeId, status, startDate',
      employeeLoans: '++id, companyId, employeeId, status',
      employeeAttendance: '++id, companyId, employeeId, date',
      salaryComponents: '++id, companyId, name, type, [companyId+name]',
      payroll: '++id, companyId, employeeId, month, year, status',
      currencies: '++id, companyId, code, isBaseCurrency, [companyId+code]',
      voucherTypes: '++id, companyId, name, [companyId+name]',
      auditLogs: '++id, companyId, actionType, entityType, entityId, timestamp',
      costCentres: '++id, companyId, name, parentCentreId, [companyId+name]',
      quotations: '++id, companyId, quotationNumber, date, status',
    });
  }
}

export const db = new AccountingDB();
